/**
 * BookingForm - Inline booking form
 * On confirm: creates lead via API → moves to "booked" pipeline stage
 */
import { useState } from "react";
import { Box, Typography, InputBase, CircularProgress } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { createLeadApi, updateGradeApi } from "../../../api/leads.api";

export default function BookingForm({ userName, userPhone, onSubmit, onCancel }) {
  const theme = useTheme();
  const p = theme.palette.primary.main;
  const [formData, setFormData] = useState({
    name: userName || "",
    phone: userPhone || "",
    date: "",
    time: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return setError("Name is required");
    if (!formData.phone.trim() || formData.phone.length < 10)
      return setError("Valid phone is required");
    if (!formData.date) return setError("Please select a date");
    if (!formData.time) return setError("Please select a time");
    if (saving) return;

    setSaving(true);
    setError("");

    try {
      // 1. Create lead in the system
      const leadPayload = {
        client_name: formData.name.trim(),
        contact: formData.phone.trim(),
        custom_fields: {
          message: `Chatbot Booking: ${formData.date} at ${formData.time}`,
          lead_source: "Chatbot",
        },
      };

      const res = await createLeadApi(leadPayload);
      const leadId = res?.data?.data?._id || res?.data?._id;

      // 2. Move lead to "booked" pipeline stage
      if (leadId) {
        await updateGradeApi(leadId, {
          key: "booked",
          type: "pipeline",
        });
      }

      // 3. Notify parent with success
      onSubmit({ ...formData, leadId, status: "success" });
    } catch (err) {
      console.error("Booking error:", err);
      // Still notify parent even if API fails (graceful degradation)
      onSubmit({ ...formData, leadId: null, status: "pending" });
    } finally {
      setSaving(false);
    }
  };

  // Name & phone are locked (from welcome form), only date & time editable
  const fields = [
    { key: "name", label: "Full Name", placeholder: "Your name", type: "text", locked: !!userName },
    { key: "phone", label: "Phone Number", placeholder: "Your phone number", type: "tel", locked: !!userPhone },
    { key: "date", label: "Preferred Date", placeholder: "", type: "date", locked: false },
    { key: "time", label: "Preferred Time", placeholder: "", type: "time", locked: false },
  ];

  return (
    <Box
      sx={{
        mx: 0.5,
        mb: 1.5,
        p: 1.8,
        borderRadius: "16px",
        bgcolor: "#fff",
        border: `1.5px solid ${alpha(p, 0.15)}`,
        boxShadow: `0 2px 12px ${alpha(p, 0.06)}`,
        animation: "araUp 0.3s ease",
        "@keyframes araUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "8px",
            bgcolor: alpha(p, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
          }}
        >
          📅
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>
          Book a Meeting
        </Typography>
      </Box>

      {/* Fields */}
      {fields.map((f) => (
        <Box key={f.key} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#777", mb: 0.3, pl: 0.3 }}>
            {f.label}
          </Typography>
          <InputBase
            type={f.type}
            value={formData[f.key]}
            onChange={(e) =>
              handleChange(
                f.key,
                f.key === "phone" ? e.target.value.replace(/[^0-9+]/g, "") : e.target.value
              )
            }
            placeholder={f.placeholder}
            fullWidth
            readOnly={f.locked}
            disabled={saving}
            sx={{
              fontSize: 12,
              px: 1.2,
              py: 0.6,
              borderRadius: "10px",
              bgcolor: f.locked ? alpha(p, 0.04) : "#f7f8fa",
              border: `1px solid ${f.locked ? alpha(p, 0.15) : alpha(p, 0.1)}`,
              color: f.locked ? "#555" : "inherit",
              "&.Mui-focused": { borderColor: f.locked ? alpha(p, 0.15) : alpha(p, 0.4) },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          />
        </Box>
      ))}

      {/* Error */}
      {error && (
        <Typography sx={{ fontSize: 10, color: "#e53935", mb: 0.8, pl: 0.3, fontWeight: 500 }}>
          {error}
        </Typography>
      )}

      {/* Buttons */}
      <Box sx={{ display: "flex", gap: 0.8, mt: 0.5 }}>
        <Box
          onClick={saving ? undefined : onCancel}
          sx={{
            flex: 1,
            py: 0.7,
            borderRadius: "10px",
            border: `1px solid #ddd`,
            textAlign: "center",
            cursor: saving ? "default" : "pointer",
            opacity: saving ? 0.5 : 1,
            "&:hover": saving ? {} : { bgcolor: "#f5f5f5" },
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#888" }}>Cancel</Typography>
        </Box>
        <Box
          onClick={handleSubmit}
          sx={{
            flex: 1,
            py: 0.7,
            borderRadius: "10px",
            background: `linear-gradient(135deg, ${p}, ${alpha(p, 0.75)})`,
            textAlign: "center",
            cursor: saving ? "default" : "pointer",
            transition: "all 0.15s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            "&:hover": saving
              ? {}
              : { transform: "translateY(-1px)", boxShadow: `0 3px 10px ${alpha(p, 0.25)}` },
          }}
        >
          {saving && <CircularProgress size={12} sx={{ color: "#fff" }} />}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>
            {saving ? "Booking..." : "Confirm"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
